/* eslint-disable react/jsx-handler-names */
import { FieldErrors } from "react-hook-form";

// Components
import Input, { InputProps } from "components/atoms/Input";
import InputMask, { InputMaskProps } from "components/atoms/InputMask";
import Button from "components/atoms/Button";
import SuccessNote from "components/atoms/SuccessNote";
import InputPhone from "components/atoms/InputPhone";

// Constants
import { timeZone } from "constants/timeZone";

// Helpers
import { birthDateValidate } from "helpers/date";
import { getNameError } from "helpers/errors";

// Hooks
import useTranslate from "hooks/useTranslate";
import { useBreakpoint } from "hooks/useBreakpoint";

// Types
import { AccountPersonalDataType } from "types/profile";
import { PhoneCountry } from "types/PhoneCountries";

// Validators
import { AccountDataFormValidation } from "../../../validators/AccountDataForm";

import "./styles.sass";

interface IPersonalDataBlock {
  data: AccountPersonalDataType;
  errors: FieldErrors;
  onEmailChange: () => void;
  onPasswordChange: () => void;
  onPhoneCountryChange: (value: PhoneCountry) => void;
  handleBlur: (value: string, props: InputProps | InputMaskProps) => void;
  isLoading: boolean;
  success: boolean;
}

const PersonalDataBlock: React.FC<IPersonalDataBlock> = ({
  success,
  data,
  errors,
  isLoading,
  handleBlur,
  onEmailChange,
  onPasswordChange,
  onPhoneCountryChange: handlePhoneCountryChange,
}) => {
  const {
    id,
    name,
    country,
    login,
    birthday,
    currency,
    phoneNumber,
    phoneCountry,
  } = data;
  const t = useTranslate();
  const { isDesktop } = useBreakpoint();

  if (!id) {
    return null;
  }

  return (
    <div className="PersonalDataBlock">
      <div className="PersonalDataBlock-data">
        <div className="PersonalDataBlock-data__id">
          <Input
            name="id"
            value={id ? id.toString() : ""}
            label={t("profile.account.id")}
            disabled
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__name">
          <Input
            id="personal-data-block-form-name"
            name="name"
            label={t("profile.account.firstname")}
            value={name}
            placeholder={t("profile.account.firstname")}
            validation={AccountDataFormValidation.name}
            error={t(getNameError(errors, "name"))}
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__country">
          <Input
            name="country"
            label={t("profile.account.country")}
            disabled
            value={country}
            placeholder={t("profile.account.country")}
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__phone">
          <InputPhone
            value={phoneNumber}
            phoneCountry={phoneCountry}
            countryName={country}
            error={t(getNameError(errors, "phoneNumber"))}
            onCountryChange={handlePhoneCountryChange}
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__email" onClick={onEmailChange}>
          <Input
            label={t("profile.balance.withdraw.email")}
            name="login"
            value={login}
            disabled
            editable
            type="email"
            placeholder="username@hostname"
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__birthday">
          <InputMask
            type="tel"
            label={t("profile.account.birthday")}
            name="birthday"
            mask="00-00-0000"
            value={birthday}
            error={t(getNameError(errors, "birthday"))}
            validation={{
              validate: (value) => {
                if (!value) {
                  return true;
                }

                return birthDateValidate(birthday || value);
              },
            }}
            placeholder={t("profile.account.birthday.type")}
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__currency">
          <Input
            label={t("profile.settings.timezone.title")}
            name="timezone"
            value={timeZone}
            disabled
            onBlur={handleBlur}
          />
        </div>
        <div
          className="PersonalDataBlock-data__password"
          // eslint-disable-next-line react/jsx-handler-names
          onClick={onPasswordChange}
        >
          <Input
            name="password"
            value="••••••••••••"
            label={t("profile.settings.password")}
            disabled
            editable
          />
        </div>
        <div className="PersonalDataBlock-data__currency">
          <Input
            label={t("profile.account.currency.title")}
            name="currency"
            value={currency || ""}
            disabled
            onBlur={handleBlur}
          />
        </div>
        <div className="PersonalDataBlock-data__submit">
          <Button
            htmlType="submit"
            label={t("profile.account.button")}
            variation="primary"
            loading={isLoading}
          />
        </div>
      </div>
      {success && !isDesktop && <SuccessNote type="account" />}
    </div>
  );
};

export default PersonalDataBlock;
